import React, { useState, useEffect } from 'react';
import { FaTimes, FaPassport, FaExclamationCircle, FaCheckCircle } from 'react-icons/fa';

const VisaModal = ({ isOpen, onClose, onAdd, editingItem }) => {
  const [formData, setFormData] = useState({
    name: '',
    country: '',
    visaType: '',
    duration: '',
    processingTime: '',
    applicationDate: '',
    travelDate: '',
    price: '',
    status: 'Pending'
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const pad = (num) => String(num).padStart(2, '0');
  const formatDate = (date) => {
    const d = new Date(date);
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  };

  useEffect(() => {
    if (editingItem) {
      setFormData(editingItem);
    } else {
      const today = new Date();
      const inTwoWeeks = new Date(today);
      inTwoWeeks.setDate(today.getDate() + 14);
      setFormData({
        name: '',
        country: '',
        visaType: '',
        duration: '',
        processingTime: '',
        applicationDate: formatDate(today),
        travelDate: formatDate(inTwoWeeks),
        price: '',
        status: 'Pending'
      });
    }
    setErrors({});
  }, [editingItem, isOpen]);

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) newErrors.name = 'Service name is required';
    if (!formData.country.trim()) newErrors.country = 'Destination country is required';
    if (!formData.visaType) newErrors.visaType = 'Visa type is required';
    if (!formData.duration) newErrors.duration = 'Duration is required';
    if (!formData.processingTime) newErrors.processingTime = 'Processing time is required';
    if (!formData.applicationDate) newErrors.applicationDate = 'Application date is required';
    if (!formData.travelDate) newErrors.travelDate = 'Travel date is required';
    if (!formData.price.trim()) newErrors.price = 'Service fee is required';
    
    // Validate travel date is after application date
    if (formData.applicationDate && formData.travelDate) {
      const appDate = new Date(formData.applicationDate);
      const travelDate = new Date(formData.travelDate);
      if (appDate >= travelDate) {
        newErrors.travelDate = 'Travel date must be after application date';
      }
    }
    
    // Validate price format
    if (formData.price && !/^\$?\d+(\.\d{2})?$/.test(formData.price)) {
      newErrors.price = 'Please enter a valid price (e.g., $120 or 120)';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // Simulate API call delay
      await new Promise(resolve => setTimeout(resolve, 500));
      
      onAdd(formData);
      onClose();
    } catch (error) {
      console.error('Error submitting visa service:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 !m-0 z-50 flex items-center justify-center bg-black bg-opacity-60 backdrop-blur-md p-4 animate-fadeIn">
      <div className="bg-white rounded-2xl shadow-2xl max-w-5xl w-full max-h-[90vh] flex flex-col transform transition-all duration-300 scale-100">
        {/* Enhanced Header */}
        <div className="bg-gradient-to-r from-red-600 via-rose-700 to-pink-700 p-8 rounded-t-2xl relative overflow-hidden">
          <div className="absolute inset-0 bg-black bg-opacity-10"></div>
          <div className="relative flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="bg-white bg-opacity-20 p-3 rounded-xl backdrop-blur-sm border border-white border-opacity-30">
                <FaPassport className="h-7 w-7 text-white" />
              </div>
              <div>
                <h3 className="text-2xl font-display font-bold text-white">
                  {editingItem ? 'Edit Visa Service' : 'Add New Visa Service'}
                </h3>
                <p className="text-red-100 text-lg">Complete visa application details</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-white hover:bg-white hover:bg-opacity-20 p-3 rounded-xl transition-all duration-200 hover:scale-105"
            >
              <FaTimes className="h-6 w-6" />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto">
          <form id="visa-form" onSubmit={handleSubmit} className="p-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-secondary-700 mb-2">
                Service Name
              </label>
              <input
                type="text"
                required
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                className="input-field"
                placeholder="e.g., Business Visa Application"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-secondary-700 mb-2">
                Destination Country
              </label>
              <input
                type="text"
                required
                value={formData.country}
                onChange={(e) => setFormData(prev => ({ ...prev, country: e.target.value }))}
                className="input-field"
                placeholder="e.g., United Kingdom"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-secondary-700 mb-2">
                Visa Type
              </label>
              <select
                value={formData.visaType}
                onChange={(e) => setFormData(prev => ({ ...prev, visaType: e.target.value }))}
                className="input-field"
              >
                <option value="">Select Visa Type</option>
                <option value="Tourist">Tourist</option>
                <option value="Business">Business</option>
                <option value="Transit">Transit</option>
                <option value="Student">Student</option>
                <option value="Work">Work</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-secondary-700 mb-2">
                Duration
              </label>
              <select
                value={formData.duration}
                onChange={(e) => setFormData(prev => ({ ...prev, duration: e.target.value }))}
                className="input-field"
              >
                <option value="">Select Duration</option>
                <option value="Single Entry">Single Entry</option>
                <option value="Multiple Entry - 30 days">Multiple Entry - 30 days</option>
                <option value="Multiple Entry - 90 days">Multiple Entry - 90 days</option>
                <option value="Multiple Entry - 1 year">Multiple Entry - 1 year</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-secondary-700 mb-2">
                Processing Time
              </label>
              <select
                value={formData.processingTime}
                onChange={(e) => setFormData(prev => ({ ...prev, processingTime: e.target.value }))}
                className="input-field"
              >
                <option value="">Select Processing Time</option>
                <option value="Standard (5-7 days)">Standard (5-7 days)</option>
                <option value="Express (2-3 days)">Express (2-3 days)</option>
                <option value="Rush (24 hours)">Rush (24 hours)</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-secondary-700 mb-2">
                Status
              </label>
              <select
                value={formData.status}
                onChange={(e) => setFormData(prev => ({ ...prev, status: e.target.value }))}
                className="input-field"
              >
                <option value="Pending">Pending</option>
                <option value="In Process">In Process</option>
                <option value="Approved">Approved</option>
                <option value="Rejected">Rejected</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-secondary-700 mb-2">
                Application Date
              </label>
              <input
                type="date"
                required
                value={formData.applicationDate}
                onChange={(e) => setFormData(prev => ({ ...prev, applicationDate: e.target.value }))}
                className="input-field"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-secondary-700 mb-2">
                Intended Travel Date
              </label>
              <input
                type="date"
                required
                value={formData.travelDate}
                onChange={(e) => setFormData(prev => ({ ...prev, travelDate: e.target.value }))}
                className="input-field"
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-secondary-700 mb-2">
                Service Fee
              </label>
              <input
                type="text"
                required
                value={formData.price}
                onChange={(e) => setFormData(prev => ({ ...prev, price: e.target.value }))}
                className="input-field"
                placeholder="e.g., $120"
              />
            </div>
          </div>

          </form>
        </div>
        {/* Enhanced Footer */}
        <div className="flex-shrink-0 bg-white border-t border-gray-200 p-8">
          <div className="flex flex-col sm:flex-row gap-4">
            <button
              type="submit"
              form="visa-form"
              disabled={isSubmitting}
              className="flex-1 bg-gradient-to-r from-red-600 via-rose-700 to-pink-700 text-white py-4 px-8 rounded-xl font-semibold text-lg hover:from-red-700 hover:via-rose-800 hover:to-pink-800 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none disabled:shadow-lg flex items-center justify-center space-x-2"
            >
              {isSubmitting ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  <span>Processing...</span>
                </>
              ) : (
                <>
                  <FaCheckCircle className="h-5 w-5" />
                  <span>{editingItem ? 'Update Visa Service' : 'Add Visa Service to Booking'}</span>
                </>
              )}
            </button>
            <button
              onClick={onClose}
              disabled={isSubmitting}
              className="flex-1 bg-gray-100 text-gray-700 py-4 px-8 rounded-xl font-semibold text-lg hover:bg-gray-200 transition-all duration-300 border border-gray-300 hover:border-gray-400 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
            >
              <FaTimes className="h-5 w-5" />
              <span>Cancel</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VisaModal;